import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createSchedule,
  deleteSchedule,
  fetchDailyReport,
  fetchDailySchedules,
  getSchedulerContext,
  hasSchedulerContext,
  submitDailySchedule,
  updateSchedule,
  updateScheduleStatus,
} from '../services/schedulerApi';

const useScheduler = (studyDate) => {
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [dailyReport, setDailyReport] = useState(null);
  const requestIdRef = useRef(0);
  const schedulerContext = getSchedulerContext();
  const isAuthenticated = hasSchedulerContext();

  const loadPlans = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!hasSchedulerContext()) {
      setPlans([]);
      setDailyReport(null);
      setError('');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    const [response, reportResponse] = await Promise.all([
      fetchDailySchedules(studyDate),
      fetchDailyReport(studyDate),
    ]);
    if (requestId !== requestIdRef.current) return;

    if (response?.error) {
      setPlans([]);
      setError(response.error);
    } else {
      setPlans(Array.isArray(response) ? response : []);
    }
    setDailyReport(reportResponse?.error ? null : reportResponse);
    setIsLoading(false);
  }, [studyDate]);

  useEffect(() => {
    loadPlans();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadPlans]);

  const savePlan = async (plan) => {
    const response = plan.sno ? await updateSchedule(plan) : await createSchedule(plan);
    if (response?.error) throw new Error(response.error);
    await loadPlans();
  };

  const removePlan = async (sno) => {
    const response = await deleteSchedule(sno);
    if (response?.error) throw new Error(response.error);
    await loadPlans();
  };

  const changeStatus = async (sno, status) => {
    const response = await updateScheduleStatus(sno, status);
    if (response?.error) throw new Error(response.error);
    await loadPlans();
  };

  const submitDay = async () => {
    const response = await submitDailySchedule(studyDate);
    if (response?.error) throw new Error(response.error);
    await loadPlans();
    return response;
  };

  return {
    changeStatus,
    dailyReport,
    error,
    isLoading,
    isAuthenticated,
    loadPlans,
    plans,
    removePlan,
    savePlan,
    submitDay,
    studentName: schedulerContext.studentName,
  };
};

export default useScheduler;
